import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/stores/hooks';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown, Lock, Settings, Unlock } from 'lucide-react';
import { cn } from '@/lib/utils';
import cryptographerService from '../services/cryptographer-service';

interface VaultSwitcherProps {
  onVaultSelect: (vaultId: string, vaultName: string) => void;
  size?: 'default' | 'large';
}

/**
 * Vault switcher component using Command component
 * Allows users to switch between available vaults
 */
export const VaultSwitcher: React.FC<VaultSwitcherProps> = ({ onVaultSelect, size = 'default' }) => {
  const { vaults, currentVault } = useAppSelector(state => state.vault);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleSelect = (vaultId: string) => {
    const vault = vaults.find(v => v.info.id === vaultId);
    if (vault && vault.info.id !== currentVault?.info.id) {
      onVaultSelect(vault.info.id, vault.info.name);
    }
    setOpen(false);
  };

  const handleManageVaults = () => {
    navigate('/');
    setOpen(false);
  };

  const isLarge = size === 'large';
  const buttonHeight = isLarge ? 'h-12' : 'h-10';
  const textSize = isLarge ? 'text-2xl font-bold' : 'text-sm';
  const iconSize = isLarge ? 'h-6 w-6' : 'h-4 w-4';
  const popoverWidth = isLarge ? 'w-[400px]' : 'w-full';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between w-full", buttonHeight)}
        >
          <span className={cn("truncate", textSize)}>
            {currentVault?.info.name || "Select vault..."}
          </span>
          <ChevronsUpDown className={cn("ml-2 shrink-0 opacity-50", iconSize)} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0", popoverWidth)}>
        <Command>
          <CommandInput placeholder="Search vaults..." />
          <CommandList>
            <CommandEmpty>No vaults found.</CommandEmpty>
            <CommandGroup>
              {vaults.map((vault) => {
                const isUnlocked = cryptographerService.hasCryptographer(vault.info.id);
                return (
                  <CommandItem
                    key={vault.info.id}
                    value={vault.info.id}
                    onSelect={() => handleSelect(vault.info.id)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      {isUnlocked ? (
                        <Unlock className="h-4 w-4 text-green-600" />
                      ) : (
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="truncate">{vault.info.name}</span>
                    </div>
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        currentVault?.info.id === vault.info.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={handleManageVaults}
                className="text-muted-foreground"
              >
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  <span>Manage vaults...</span>
                </div>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default VaultSwitcher;